
import { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, BookOpen, Download, Rocket, Sparkles, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageTransition from "@/components/PageTransition";

const ThankYou = () => {
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isMounted, setIsMounted] = useState(false);

  // Initialize stars canvas
  useEffect(() => {
    setIsMounted(true);
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas dimensions
    const updateSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', updateSize);
    updateSize();

    // Create stars
    const stars: { x: number; y: number; radius: number; speed: number }[] = [];
    const createStars = () => {
      for (let i = 0; i < 200; i++) {
        stars.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          radius: Math.random() * 2 + 0.5,
          speed: Math.random() * 0.05
        });
      }
    };
    createStars();

    // Animate stars
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw stars
      stars.forEach(star => {
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.radius, 0, Math.PI * 2);
        ctx.fillStyle = 'white';
        ctx.fill();
        
        // Move stars
        star.y += star.speed;
        
        // Reset star position if it goes off screen
        if (star.y > canvas.height) {
          star.y = 0;
          star.x = Math.random() * canvas.width;
        }
      });
      
      requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      window.removeEventListener('resize', updateSize);
    };
  }, []);

  // Parallax effect for planets
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: (e.clientX - window.innerWidth / 2) / 25,
        y: (e.clientY - window.innerHeight / 2) / 25
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  // Check if user came from a form submission
  useEffect(() => {
    const formSubmitted = sessionStorage.getItem("formSubmitted");
    if (!formSubmitted) {
      navigate("/");
    }
  }, [navigate]);

  return (
    <PageTransition>
      <Helmet>
        <title>Thank You | Los Angeles SEO Group</title>
        <meta name="description" content="Thank you for contacting Los Angeles SEO Group. Download our free SEO whitepaper and explore our knowledge base." />
      </Helmet>

      <div className="min-h-screen flex flex-col relative overflow-hidden bg-gradient-to-b from-[#1A1F2C] via-[#2D3748] to-[#1A1F2C]">
        {/* Background stars canvas */}
        <canvas 
          ref={canvasRef} 
          className="absolute inset-0 z-0"
        />

        {/* Planets */}
        <div className="absolute inset-0 z-10 overflow-hidden pointer-events-none">
          {/* Large planet */}
          <motion.div 
            className="absolute right-[-200px] top-[-200px] w-[600px] h-[600px] rounded-full bg-gradient-to-br from-[#F97316] to-[#FB923C] opacity-40 blur-md"
            animate={{
              x: mousePosition.x * -1.5,
              y: mousePosition.y * -1.5,
            }}
            transition={{ type: "spring", damping: 15 }}
          />
          
          {/* Small blue planet */}
          <motion.div 
            className="absolute left-[10%] bottom-[10%] w-[150px] h-[150px] rounded-full bg-gradient-to-br from-[#0EA5E9] to-[#38BDF8] opacity-40 blur-sm"
            animate={{
              x: mousePosition.x * 2,
              y: mousePosition.y * 2,
            }}
            transition={{ type: "spring", damping: 15 }}
          />
          
          {/* Purple nebula */}
          <motion.div 
            className="absolute left-[-100px] top-[30%] w-[300px] h-[300px] rounded-full bg-gradient-to-br from-[#8B5CF6] to-[#A78BFA] opacity-30 blur-lg"
            animate={{
              x: mousePosition.x,
              y: mousePosition.y,
            }}
            transition={{ type: "spring", damping: 15 }}
          />
        </div>

        {/* Navbar */}
        <Navbar />

        {/* Main content */}
        <div className="flex-grow flex items-center justify-center relative z-20 px-4 py-16">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-8 flex justify-center"
            >
              <div className="relative">
                <Rocket className="h-16 w-16 text-white" />
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                  className="absolute -bottom-4 -left-1 w-20 h-10 bg-gradient-to-t from-[#F97316] to-transparent opacity-60 blur-md rounded-full"
                />
              </div>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-4xl md:text-6xl font-bold mb-6 text-white flex flex-col md:flex-row items-center justify-center gap-2"
            >
              <span>Thank</span>
              <span className="flex items-center">
                <span>You</span>
                <motion.span
                  animate={{ rotate: [0, 15, -15, 0] }}
                  transition={{ repeat: Infinity, duration: 5 }}
                >
                  <Star className="h-8 w-8 ml-2 text-[#F97316]" />
                </motion.span>
              </span>
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="glass-card p-6 md:p-8 mb-12 backdrop-blur-md bg-white/10 rounded-xl border border-white/20"
            >
              <Sparkles className="h-6 w-6 text-[#F97316] mx-auto mb-4" />
              <h2 className="text-xl md:text-2xl font-bold text-white mb-4">Your message has been received!</h2>
              <p className="text-white/80 text-lg mb-6">
                Our SEO experts will review your information and get back to you shortly. 
                In the meantime, download our free whitepaper to start improving your rankings today.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white/5 backdrop-blur-sm p-6 rounded-lg border border-white/10 flex flex-col items-center text-center hover:bg-white/10 transition-all">
                  <Download className="h-10 w-10 text-[#0EA5E9] mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2">SEO Whitepaper</h3>
                  <p className="text-white/70 text-sm mb-4">
                    "10 Proven Strategies to Boost Your Local SEO Rankings in 2025"
                  </p>
                  <Button 
                    variant="default" 
                    className="mt-auto bg-[#0EA5E9] hover:bg-[#0EA5E9]/80 text-white"
                    asChild
                  >
                    <a href="/whitepaper-2025-seo-strategies.pdf" download>
                      Download PDF <ArrowRight className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                </div>

                <div className="bg-white/5 backdrop-blur-sm p-6 rounded-lg border border-white/10 flex flex-col items-center text-center hover:bg-white/10 transition-all">
                  <BookOpen className="h-10 w-10 text-[#8B5CF6] mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2">Knowledge Base</h3>
                  <p className="text-white/70 text-sm mb-4">
                    Explore our extensive SEO resources and stay ahead of the latest industry trends.
                  </p>
                  <Button 
                    variant="outline" 
                    className="mt-auto border-[#8B5CF6] text-[#8B5CF6] hover:bg-[#8B5CF6]/10"
                    asChild
                  >
                    <Link to="/knowledge-base">
                      Explore Resources <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex flex-col md:flex-row items-center justify-center gap-6"
            >
              <Button 
                variant="ghost" 
                className="text-white hover:bg-white/10"
                asChild
              >
                <Link to="/">
                  Return to Home
                </Link>
              </Button>
              
              <Button 
                variant="ghost" 
                className="text-white hover:bg-white/10"
                asChild
              >
                <Link to="/contact">
                  Contact Us
                </Link>
              </Button>
            </motion.div>
          </div>
        </div>

        {/* Footer */}
        <Footer />
      </div>
    </PageTransition>
  );
};

export default ThankYou;
